import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { getCommandManager } from '../../../hooks/useCommandManager';
import { useDiagramStore } from '../../../stores/diagramStore';
import { cn } from '../../../lib/utils';
import {
  useCompassViewPrefs,
  DEFAULT_ZOOM,
  type FocusSpec,
} from '../../../stores/compassViewPrefsStore';
import {
  ChangeFocusCommand,
  ChangeZoomLevelCommand,
  ToggleFrameExpandedCommand,
} from '../commands/CompassExplorerCommands';

/**
 * Compass Explorer breadcrumb. Renders the active focus chain plus any
 * in-place-expanded frames as a horizontal row of clickable segments in
 * the normal app header.
 *
 *   Overview  ›  Account: prod  ›  VPC: creately-stage-4  ›  subnet-a
 *
 * Click any segment to collapse everything below it. Click "Overview"
 * to return to the L1 bubble grid. Focus changes route through
 * `ChangeFocusCommand` so undo doubles as "back."
 *
 * Only renders when there is meaningful context — plain overview +
 * no expanded frames hides the breadcrumb so it doesn't clutter the
 * default view.
 */

type TFn = (key: string, opts?: Record<string, unknown>) => string;

function focusLabel(focus: FocusSpec, t: TFn): string {
  switch (focus.kind) {
    case 'overview':
      return t('compass.breadcrumb.overview');
    case 'account':
      return t('compass.breadcrumb.account', { value: focus.label ?? focus.accountId });
    case 'vpc':
      return t('compass.breadcrumb.vpc', { value: focus.label ?? focus.vpcId });
    case 'subnet':
      return t('compass.breadcrumb.subnet', { value: focus.label ?? focus.subnetId });
    case 'resource':
      return t('compass.breadcrumb.resource', { value: focus.label ?? focus.resourceId });
    case 'preset':
      return t('compass.breadcrumb.preset', { value: focus.label ?? focus.presetId });
  }
}

export const CompassBreadcrumb: React.FC<{ className?: string }> = ({ className }) => {
  const { t } = useTranslation('diagram');
  const focus = useCompassViewPrefs((s) => s.focus);
  const expandedFrameIds = useCompassViewPrefs((s) => s.expandedFrameIds);
  const nodes = useDiagramStore((s) => s.nodes);

  // Map expanded ids → human labels by looking up the bubble/frame
  // node in the scene. Falls back to the trailing segment of the id
  // when the node isn't present (e.g. just after a focus change).
  const labelForId = useMemo(() => {
    const out = new Map<string, string>();
    for (const n of nodes) {
      const label = n.name
        ?? (typeof n.attrs?.label === 'string' ? n.attrs.label : null);
      if (label) {
        out.set(n.id, String(label));
        // Also allow lookups keyed by raw reference id when expansion
        // was recorded as `bubble:<ref>` but the set carries the ref.
        const ref = typeof n.attrs?.referenceId === 'string'
          ? n.attrs.referenceId
          : undefined;
        if (ref) out.set(ref, String(label));
      }
    }
    return out;
  }, [nodes]);

  const segments = useMemo(() => {
    const parts: Array<{ label: string; onClick?: () => void }> = [];
    // Always show Overview when we're anywhere deeper than overview.
    const inDeeper = focus.kind !== 'overview';
    if (inDeeper) {
      parts.push({
        label: t('compass.breadcrumb.overview'),
        onClick: () => {
          const cm = getCommandManager();
          cm.execute(new ChangeFocusCommand({ kind: 'overview' }));
          if (useCompassViewPrefs.getState().zoomLevel !== DEFAULT_ZOOM) {
            cm.execute(new ChangeZoomLevelCommand(DEFAULT_ZOOM));
          }
        },
      });
    }
    // Current focus
    parts.push({
      label: focusLabel(focus, t),
      onClick: inDeeper
        ? undefined
        : () => {
            // At overview with expanded frames — clicking the root
            // segment collapses every expanded frame.
            const cm = getCommandManager();
            for (const id of expandedFrameIds.slice().reverse()) {
              cm.execute(new ToggleFrameExpandedCommand(id));
            }
          },
    });
    // Expanded frames, in expansion order — click an earlier segment
    // collapses everything below it.
    expandedFrameIds.forEach((id, i) => {
      const human = labelForId.get(id)
        ?? labelForId.get(id.replace(/^bubble:/, ''))
        ?? labelForId.get(id.replace(/^frame:/, ''))
        ?? id.replace(/^(bubble|frame):/, '');
      parts.push({
        label: human,
        onClick: () => {
          const cm = getCommandManager();
          const deeper = expandedFrameIds.slice(i).reverse();
          for (const toClose of deeper) {
            cm.execute(new ToggleFrameExpandedCommand(toClose));
          }
        },
      });
    });
    return parts;
  }, [focus, expandedFrameIds, labelForId, t]);

  // Hide entirely when we're at a plain Overview with nothing expanded.
  if (focus.kind === 'overview' && expandedFrameIds.length === 0) {
    return null;
  }

  return (
    <nav
      className={cn('flex items-center gap-1 text-sm', className)}
      aria-label={t('compass.breadcrumb.ariaLabel')}
    >
      {segments.map((seg, i) => (
        <React.Fragment key={`${seg.label}-${i}`}>
          {i > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground" aria-hidden />}
          {seg.onClick ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={seg.onClick}
              className="h-7 px-2 text-xs"
            >
              {seg.label}
            </Button>
          ) : (
            <span className="px-2 text-xs font-medium text-foreground">{seg.label}</span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
};
